import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ASSETS, CONTACT } from "../lib/brand";

const QUOTES = [
  {
    q: "A Herida entende de moda como pouca gente. Ela transforma o que eu não usava mais em algo que outra pessoa vai amar. É serviço, é afeto, é arte.",
    name: "Camila R.",
    role: "Cliente & consignante",
  },
  {
    q: "Cheguei perdida no meu guarda-roupa e saí com um estilo. A consultoria dela é uma virada de chave. Recomendo sem pensar duas vezes.",
    name: "Débora M.",
    role: "Empresária",
  },
  {
    q: "Já falamos sobre ela na rádio e na TV — a Herida é uma das vozes mais consistentes do movimento de moda circular do Sul.",
    name: "Redação",
    role: "Imprensa parceira",
  },
];

function GoogleG({ size = 22 }) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function Star({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 17.27l6.18 3.73-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  );
}

export default function Depoimentos() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <section
      id="feedback"
      ref={ref}
      data-testid="depoimentos-section"
      className="relative bg-brick-800 text-cream py-24 sm:py-40 overflow-hidden"
    >
      {/* Massive background word */}
      <div
        aria-hidden
        className="absolute -top-10 sm:-top-20 -right-10 pointer-events-none select-none opacity-10"
      >
        <span
          className="font-serif italic leading-none"
          style={{ fontSize: "clamp(10rem, 22vw, 24rem)" }}
        >
          confiam
        </span>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-6 sm:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 sm:mb-20">
          <div>
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/80 mb-8">
              <span className="w-8 h-px bg-cream/60" />
              <span>N.º 004 — Feedback</span>
              <span className="inline-flex items-center gap-2 text-cream/80">
                <span className="w-1.5 h-1.5 bg-cream rounded-full animate-pulse" />
                depoimentos reais
              </span>
            </div>
            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "110%" }}
                animate={inView ? { y: 0 } : {}}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif leading-[0.9] tracking-tight text-cream"
                style={{ fontSize: "clamp(3rem, 8vw, 8rem)" }}
              >
                Vozes que <span className="italic-serif">confiam</span>.
              </motion.h2>
            </div>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="max-w-md font-sans text-base sm:text-lg leading-relaxed text-cream/85"
          >
            Cada retorno eu registro, guardo e compartilho.
            Vídeos reais dos meus clientes + as avaliações no Google —
            duas provas vivas da experiência.
          </motion.p>
        </div>

        {/* Two proof cards: Feedback video + Google reviews */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Feedback video card */}
          <motion.a
            href={CONTACT.feedback}
            target="_blank"
            rel="noreferrer"
            aria-label="Assistir feedback dos clientes"
            data-testid="feedback-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="group relative block bg-cream text-ink-800 border border-cream overflow-hidden"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-cream-200">
              <img
                src={ASSETS.hangers}
                alt="Prévia — feedback dos clientes"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(26,24,24,0) 30%, rgba(26,24,24,0.6) 100%)",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 rounded-full border border-cream"
                    animate={{ scale: [1, 1.35, 1], opacity: [0.65, 0, 0.65] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                  />
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 rounded-full border border-cream/60"
                    animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
                  />
                  <span className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-brick-500 text-cream flex items-center justify-center group-hover:bg-cream group-hover:text-brick-500 transition-colors duration-500">
                    <svg
                      viewBox="0 0 24 24"
                      width="26"
                      height="26"
                      fill="currentColor"
                      aria-hidden="true"
                      className="ml-1"
                    >
                      <path d="M8 5.14v13.72a1 1 0 001.55.83l10.03-6.86a1 1 0 000-1.66L9.55 4.31A1 1 0 008 5.14z" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="absolute top-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brick-500 rounded-full animate-pulse" />
                  Feedback real
                </span>
                <span>HL / Clientes</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-5 border-t border-ink-800/15">
              <div className="flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brick-500">
                  Stories · destacados
                </span>
                <span
                  className="mt-1 font-serif italic leading-none"
                  style={{ fontSize: "clamp(1.35rem, 1.7vw, 1.75rem)" }}
                >
                  Ouça quem já viveu
                </span>
              </div>
              <span className="font-serif italic text-3xl text-brick-500 group-hover:translate-x-1 transition-transform duration-500">
                →
              </span>
            </div>
          </motion.a>

          {/* Google Reviews card */}
          <motion.a
            href={CONTACT.googleReviews}
            target="_blank"
            rel="noreferrer"
            aria-label="Ver avaliações no Google"
            data-testid="google-reviews-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="group relative block bg-cream text-ink-800 border border-cream overflow-hidden"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-cream-200">
              <img
                src={ASSETS.reviews}
                alt="Herida — leia as avaliações no Google"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(26,24,24,0) 30%, rgba(26,24,24,0.6) 100%)",
                }}
              />

              {/* Google badge */}
              <div className="absolute top-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream">
                <span className="inline-flex items-center gap-2 bg-cream text-ink-800 px-3 py-1.5 shadow-sm">
                  <GoogleG size={14} /> Google
                </span>
                <span>Avaliações</span>
              </div>

              {/* Floating stars badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-4 right-4 bottom-4 flex flex-col gap-2 bg-cream/95 backdrop-blur-md px-4 py-4 border border-cream"
              >
                <div className="flex items-center gap-2">
                  <GoogleG size={20} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/70">
                    Avaliações verificadas
                  </span>
                </div>
                <div className="flex items-center gap-2 text-brick-500">
                  <Star />
                  <Star />
                  <Star />
                  <Star />
                  <Star />
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60">
                    Avaliação máxima
                  </span>
                </div>
              </motion.div>
            </div>

            <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-5 border-t border-ink-800/15">
              <div className="flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brick-500">
                  Reputação · Google
                </span>
                <span
                  className="mt-1 font-serif italic leading-none"
                  style={{ fontSize: "clamp(1.35rem, 1.7vw, 1.75rem)" }}
                >
                  Leia todas as avaliações
                </span>
              </div>
              <span className="font-serif italic text-3xl text-brick-500 group-hover:translate-x-1 transition-transform duration-500">
                →
              </span>
            </div>
          </motion.a>
        </div>

        {/* Text testimonials */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
          {QUOTES.map((t, i) => (
            <motion.blockquote
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.8,
                delay: 0.9 + i * 0.15,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="relative flex flex-col gap-5"
              data-testid={`depoimento-${i}`}
            >
              <span className="font-serif italic text-brick-500 text-7xl leading-none">
                “
              </span>
              <p
                className="font-serif italic leading-snug text-cream"
                style={{ fontSize: "clamp(1.1rem, 1.35vw, 1.5rem)" }}
              >
                {t.q}
              </p>
              <div className="mt-2 pt-4 border-t border-cream/20 font-mono text-[11px] uppercase tracking-[0.24em] text-cream/85">
                <div>{t.name}</div>
                <div className="opacity-60 mt-1">{t.role}</div>
              </div>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
